/* ============================================================
   Deal Management — API service layer.
   Thin wrapper over the app's shared request helpers (getRequest /
   postRequest / putRequest from src/config) that centralises every
   /api/deal endpoint. Reads return the response `body`; actions return
   the full response so callers can read `code`, `message` and `body`.
   Per-deal reads use the 'return-response' mode so a legitimate 404
   (step not generated yet) is handled in-component rather than toasting
   or logging the user out.
   ============================================================ */
'use strict';
import { getRequest, postRequest, putRequest } from '../../../config';

// Resolve a GET into its body, or null on any non-200 (empty/not-found).
const body = res => (res && res.code === 200 ? res.body : null);

// ── Reads ─────────────────────────────────────────────────────────────────────
// Returns the full response so the pipeline screen can surface *why* a load failed
// (401 not logged in · 400 no investor firm · 5xx server) instead of a blind error.
export const fetchPipelineRes = async () => getRequest('/deal/pipeline', 'return-response');
export const fetchPipeline = async () => body(await getRequest('/deal/pipeline', 'return-response'));
export const fetchDashboard = async () => body(await getRequest('/deal/dashboard', 'return-response'));
export const fetchProfile = async () => body(await getRequest('/deal/profile', 'return-response'));
export const fetchDeal = async id => body(await getRequest(`/deal/${id}`, 'return-response'));

export const fetchIntake = async id => body(await getRequest(`/deal/${id}/intake`, 'return-response'));
export const fetchAnalysis = async id => body(await getRequest(`/deal/${id}/analysis`, 'return-response'));
export const fetchTriage = async id => body(await getRequest(`/deal/${id}/triage`, 'return-response'));
export const fetchDiligence = async id => body(await getRequest(`/deal/${id}/diligence`, 'return-response'));
export const fetchScenarios = async id => body(await getRequest(`/deal/${id}/scenarios`, 'return-response'));
export const fetchMemo = async id => body(await getRequest(`/deal/${id}/memo`, 'return-response'));
export const fetchDecision = async id => body(await getRequest(`/deal/${id}/decision`, 'return-response'));
export const fetchAudit = async id => body(await getRequest(`/deal/${id}/audit`, 'return-response'));

// ── Actions (return the full response: { code, message, body }) ────────────────
export const runAnalysis = (id) => postRequest(`/deal/${id}/analysis/run`, {}, true);        // background run; progress via sockets
export const runScenarios = (id) => postRequest(`/deal/${id}/scenarios/run`, {});
export const regenerateMemo = (id, section) => postRequest(`/deal/${id}/memo/regenerate`, section ? { section } : {});
export const saveMemo = (id, sections) => putRequest(`/deal/${id}/memo`, { sections });
export const saveTriage = (id, patch) => putRequest(`/deal/${id}/triage`, patch);
export const runTriage = (id) => postRequest(`/deal/${id}/triage/run`, {});   // AI: generate scorecard + rationale
export const advanceTriage = (id) => postRequest(`/deal/${id}/triage/advance`, {});
export const declineDeal = (id, patch) => postRequest(`/deal/${id}/decline`, patch);
export const overrideGate = (id, reason) => postRequest(`/deal/${id}/intake/override`, { reason });
export const recordDecision = (id, patch) => postRequest(`/deal/${id}/decision`, patch);
export const requestDataroom = (id, patch) => postRequest(`/deal/${id}/diligence/request`, patch);
export const remindDataroom = (id, patch) => postRequest(`/deal/${id}/diligence/remind`, patch);
export const sendQuestions = (id, patch) => postRequest(`/deal/${id}/questions`, patch);
export const shareDeal = (id, patch) => postRequest(`/deal/${id}/share`, patch);
export const inviteFounder = (id, patch) => postRequest(`/deal/${id}/invite`, patch);
export const analyseSandbox = (text) => postRequest('/deal/sandbox/analyse', { text });
export const seedDemo = () => postRequest('/deal/dev/seed', { reset: false });     // dev: populate the caller's firm with the worked example
// Add an applicant + pitch deck. The deck File is sent as `attachment` (multipart → S3);
// the rest are funding-form fields the zero-LLM gate compares against the profile.
export const addApplicant = (payload) => postRequest('/deal', payload);
export const exportPdf = (id, kind) => getRequest(`/deal/${id}/export/${kind}`, 'return-response');

// Small helper for screens: was this action response a success?
export const ok = res => !!(res && res.code === 200);
